import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { formatApiError } from "@/lib/api-error";
import { toneSoftClasses } from "@/lib/tones";
import { cn } from "@/lib/utils";
import { financeApi } from "@/services/finance-api";
import type {
  ArranjoFaturamento,
  OmieEmpresaChave,
  PatchAgenciaDaPropostaBody,
  PropostaDetail,
} from "@/types/finance";
import { formatPercent } from "@/utils/money";
import { ARRANJOS, nomeDaEmpresa } from "./arranjo-de-faturamento";
import {
  alcancaOutrasPropostas,
  errosDoRascunho,
  idDoCampoNoCadastro,
  outrasPropostasAbertas,
  patchDoRascunho,
  rascunhoDoCadastro,
  type CampoDoCadastro,
  type RascunhoDoCadastro,
} from "./dados-da-agencia";

const SEM_PADRAO = "sem_padrao";
const EMPRESAS_QUE_FATURAM: OmieEmpresaChave[] = ["corisco", "faisca", "gota_serena"];
/** Same as the largest page the proposal list accepts in the API. */
const PROPOSTAS_NA_CONSULTA = 200;
const CODIGOS_NO_AVISO = 5;

/** Same as company_arrangement in the API. */
function arranjoUsualDaEmpresa(chave: OmieEmpresaChave): ArranjoFaturamento {
  return chave === "gota_serena" ? "repasse_liquido" : "taxa_e_comissao";
}

function fraseDasOutrasPropostas(propostas: Array<{ codigo: string | null }>): string {
  const codigos = propostas
    .slice(0, CODIGOS_NO_AVISO)
    .map((proposta) => proposta.codigo ?? "sem código")
    .join(", ");
  const restantes = propostas.length - CODIGOS_NO_AVISO;
  const lista = `${codigos}${restantes > 0 ? ` e mais ${restantes}` : ""}`;
  return propostas.length === 1
    ? `Esta alteração pode mudar outra proposta aberta da agência (${lista})`
    : `Esta alteração pode mudar outras ${propostas.length} propostas abertas da agência (${lista})`;
}

interface DadosDaAgenciaDialogProps {
  proposta: PropostaDetail;
  campoInicial: CampoDoCadastro | null;
  onSalvar: (patch: PatchAgenciaDaPropostaBody) => Promise<void>;
  onFechar: () => void;
}

export function DadosDaAgenciaDialog({ proposta, campoInicial, onSalvar, onFechar }: DadosDaAgenciaDialogProps) {
  const agencia = proposta.agencia;
  const [rascunho, setRascunho] = useState<RascunhoDoCadastro>(() => rascunhoDoCadastro(agencia));
  const [salvando, setSalvando] = useState(false);
  const [erroAoSalvar, setErroAoSalvar] = useState<string | null>(null);

  const outrasPropostasQuery = useQuery({
    queryKey: ["finance", "propostas", "da-agencia", agencia.id],
    queryFn: () => financeApi.listarPropostas({ agencia_id: agencia.id, page_size: PROPOSTAS_NA_CONSULTA }),
  });
  const outrasAbertas = outrasPropostasQuery.data
    ? outrasPropostasAbertas(outrasPropostasQuery.data.items, proposta.id)
    : null;

  const erros = errosDoRascunho(rascunho);
  const temErro = Object.keys(erros).length > 0;
  const patch = temErro ? {} : patchDoRascunho(rascunho, agencia);
  const semMudancas = Object.keys(patch).length === 0;
  const avisarOutrasPropostas = alcancaOutrasPropostas(patch) && outrasAbertas?.length !== 0;

  const mudar = (mudancas: Partial<RascunhoDoCadastro>) => setRascunho((atual) => ({ ...atual, ...mudancas }));

  const focarCampoInicial = (event: Event) => {
    if (!campoInicial) return;
    const campo =
      campoInicial === "faturamento_arranjo" && !agencia.faturamento_empresa ? "faturamento_empresa" : campoInicial;
    const elemento = document.getElementById(idDoCampoNoCadastro(campo));
    if (!elemento) return;
    event.preventDefault();
    elemento.focus();
    if (elemento instanceof HTMLInputElement) elemento.select();
  };

  const salvar = async () => {
    setSalvando(true);
    setErroAoSalvar(null);
    try {
      await onSalvar(patch);
      onFechar();
    } catch (erro) {
      const formatado = formatApiError(erro);
      setErroAoSalvar(formatado.detail ?? formatado.title);
    } finally {
      setSalvando(false);
    }
  };

  const texto = (
    campo: "sigla" | "razao_social" | "cnpj" | "endereco" | "contato_nome" | "contato_email" | "contato_cargo" | "dia_emissao_competencia" | "prazo_pagamento_competencia_dias" | "impostos_percentual",
    label: string,
    opcoes: { className?: string; placeholder?: string; inputMode?: "numeric" | "decimal"; ajuda?: ReactNode } = {},
  ) => (
    <div className={cn("space-y-1.5", opcoes.className)}>
      <Label htmlFor={idDoCampoNoCadastro(campo)} className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      <Input
        id={idDoCampoNoCadastro(campo)}
        value={rascunho[campo]}
        placeholder={opcoes.placeholder}
        inputMode={opcoes.inputMode}
        aria-invalid={!!erros[campo] || undefined}
        className={cn(erros[campo] && "border-tone-danger-fg focus-visible:ring-tone-danger-fg/30")}
        onChange={(event) => mudar({ [campo]: event.target.value })}
      />
      {erros[campo] && (
        <p role="alert" className="text-[11px] leading-snug text-tone-danger-fg">
          {erros[campo]}
        </p>
      )}
      {opcoes.ajuda && <p className="text-[11px] leading-snug text-muted-foreground">{opcoes.ajuda}</p>}
    </div>
  );

  const { dia_emissao: diaEmissao, prazo_pagamento_dias: prazoPagamento } = proposta.fatura_por_competencia_mensal;
  const impostosDaProposta = proposta.impostos_percentual;
  const descricaoDoArranjo =
    rascunho.faturamento_empresa && rascunho.faturamento_arranjo
      ? ARRANJOS.find((opcao) => opcao.valor === rascunho.faturamento_arranjo)?.descricao(
          nomeDaEmpresa(rascunho.faturamento_empresa),
        )
      : null;
  return (
    <Dialog open onOpenChange={(aberto) => !aberto && !salvando && onFechar()}>
      <DialogContent
        className="flex max-h-[85vh] w-[90vw] max-w-[640px] flex-col gap-0 overflow-hidden p-0"
        onOpenAutoFocus={focarCampoInicial}
      >
        <DialogHeader className="shrink-0 px-6 pb-4 pt-6">
          <DialogTitle>Dados para proposta da {agencia.nome}</DialogTitle>
          <DialogDescription>
            O que você salvar aqui muda o cadastro da agência, não só esta proposta. Para mudar impostos, notas no
            invite, dia de emissão, prazo ou A/C só nesta proposta, use os campos da própria aba.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-1">
          <div className="grid gap-4 sm:grid-cols-6">
            {texto("sigla", "Sigla", {
              className: "sm:col-span-2",
              placeholder: "Ex.: MTO",
              ajuda: "Vale para os próximos códigos de proposta",
            })}
            {texto("razao_social", "Razão social", { className: "sm:col-span-4" })}
            {texto("cnpj", "CNPJ", { className: "sm:col-span-2", placeholder: "00.000.000/0000-00" })}
            {texto("endereco", "Endereço da sede", { className: "sm:col-span-4" })}
            {texto("contato_nome", "Contato (aos cuidados de)", {
              className: "sm:col-span-2",
              ajuda: "Preenche o A/C das propostas novas. O A/C desta proposta fica no campo dela",
            })}
            {texto("contato_email", "E-mail do contato", { className: "sm:col-span-2" })}
            {texto("contato_cargo", "Cargo do contato", { className: "sm:col-span-2" })}

            <div className="flex items-start justify-between gap-4 rounded-md border p-3 sm:col-span-6">
              <div className="space-y-1">
                <Label htmlFor={idDoCampoNoCadastro("fatura_por_competencia_mensal")}>Fatura por competência mensal</Label>
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Ligado, o invite divide cada período em uma nota por mês.
                  {proposta.fatura_por_competencia_mensal.escolhido !== null &&
                    " Esta proposta tem escolha própria e não muda."}
                </p>
              </div>
              <Switch
                id={idDoCampoNoCadastro("fatura_por_competencia_mensal")}
                checked={rascunho.fatura_por_competencia_mensal}
                onCheckedChange={(ligado) => mudar({ fatura_por_competencia_mensal: ligado })}
              />
            </div>

            {texto("dia_emissao_competencia", "Dia de emissão da nota", {
              className: "sm:col-span-2",
              placeholder: "Ex.: 20",
              inputMode: "numeric",
              ajuda:
                diaEmissao.escolhido !== null
                  ? `Esta proposta usa o dia ${diaEmissao.escolhido} e não muda`
                  : "Dia do mês seguinte ao trabalhado",
            })}
            {texto("prazo_pagamento_competencia_dias", "Prazo de pagamento (dias)", {
              className: "sm:col-span-2",
              placeholder: "Ex.: 18",
              inputMode: "numeric",
              ajuda:
                prazoPagamento.escolhido !== null
                  ? `Esta proposta usa ${prazoPagamento.escolhido} dias e não muda`
                  : "Dias entre a nota e o pagamento",
            })}
            {texto("impostos_percentual", "Impostos (%)", {
              className: "sm:col-span-2",
              placeholder: "Sem impostos",
              inputMode: "decimal",
              ajuda:
                impostosDaProposta.escolhido !== null
                  ? `Esta proposta usa ${formatPercent(impostosDaProposta.escolhido)}% e não muda`
                  : "Sobre profissional + plataforma",
            })}

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor={idDoCampoNoCadastro("faturamento_empresa")} className="text-xs font-medium text-muted-foreground">
                Quem fatura a agência
              </Label>
              <Select
                value={rascunho.faturamento_empresa ?? SEM_PADRAO}
                onValueChange={(valor) => {
                  if (valor === SEM_PADRAO) {
                    mudar({ faturamento_empresa: null, faturamento_arranjo: null });
                    return;
                  }
                  const chave = valor as OmieEmpresaChave;
                  mudar({
                    faturamento_empresa: chave,
                    faturamento_arranjo: rascunho.faturamento_arranjo ?? arranjoUsualDaEmpresa(chave),
                  });
                }}
              >
                <SelectTrigger id={idDoCampoNoCadastro("faturamento_empresa")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_PADRAO}>Sem padrão</SelectItem>
                  {EMPRESAS_QUE_FATURAM.map((chave) => (
                    <SelectItem key={chave} value={chave}>
                      {nomeDaEmpresa(chave)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-4">
              <Label htmlFor={idDoCampoNoCadastro("faturamento_arranjo")} className="text-xs font-medium text-muted-foreground">
                Como
              </Label>
              <Select
                value={rascunho.faturamento_arranjo ?? undefined}
                disabled={!rascunho.faturamento_empresa}
                onValueChange={(valor) => mudar({ faturamento_arranjo: valor as ArranjoFaturamento })}
              >
                <SelectTrigger id={idDoCampoNoCadastro("faturamento_arranjo")}>
                  <SelectValue placeholder="Escolha quem fatura primeiro" />
                </SelectTrigger>
                <SelectContent>
                  {ARRANJOS.map((opcao) => (
                    <SelectItem key={opcao.valor} value={opcao.valor}>
                      {opcao.titulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] leading-snug text-muted-foreground sm:col-span-6">
              {descricaoDoArranjo ? `${descricaoDoArranjo} ` : ""}
              Só as propostas novas começam assim: os períodos desta proposta não mudam.
            </p>
          </div>
        </div>

        <div className="shrink-0 space-y-3 border-t px-6 py-4">
          {avisarOutrasPropostas && (
            <div role="alert" className={cn("flex gap-2 rounded-md px-3 py-2 text-xs", toneSoftClasses.warning)}>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <p>
                {outrasAbertas ? fraseDasOutrasPropostas(outrasAbertas) : "Esta alteração pode mudar outras propostas abertas da agência"}
                , mesmo enviadas ou aceitas, quando seguem o padrão da agência. O PDF já gerado não muda: confira os
                valores e gere uma nova versão quando precisar.
              </p>
            </div>
          )}

          {erroAoSalvar && (
            <p role="alert" className="text-xs text-tone-danger-fg">
              {erroAoSalvar}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" disabled={salvando} onClick={onFechar}>
              Cancelar
            </Button>
            <Button type="button" disabled={semMudancas || temErro || salvando} onClick={() => void salvar()}>
              {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar no cadastro da agência
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
